import type { FC } from 'react';
import React, { useState, useEffect } from 'react';
import { Stack, CircularProgress, Alert, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { keepPreviousData } from '@tanstack/react-query';
import { DynamicRecipientForm } from './DynamicRecipientForm';
import {
  useGetRecipientSchema,
  useGetPayoutMethods,
  useValidateRecipientData,
  useGetAllCountriesPhoenix,
  useSortedCountries,
  transformFlatErrorsToNested,
  type RecipientData,
  type RecipientSchemaParams,
  type CreateRecipientRequest,
} from '@pflegenavi/service-provider-frontend/api';

interface RecipientFormWithSchemaProps {
  // Initial values for pre-filling the form
  initialValues?: Partial<RecipientData>;
  // Callback when form is submitted with valid data
  onSubmit: (data: CreateRecipientRequest) => Promise<void>;
  // Whether the form should be disabled
  disabled?: boolean;
  // Whether this is a re-submission of a cancelled recipient
  canResubmit?: boolean;
  // The recipient type based on legal person type
  recipientType: 'Individual' | 'Business';
  // The country code for the service provider
  countryCode?: string;
  // Currency (default EUR)
  currency?: string;
  // Callback when validation state changes
  onValidationChange?: (isValid: boolean) => void;
  // Callback when form data changes
  onDataChange?: (data: RecipientData) => void;
}

export const RecipientFormWithSchema: FC<RecipientFormWithSchemaProps> = ({
  initialValues,
  onSubmit,
  disabled = false,
  canResubmit = false,
  recipientType,
  currency = 'EUR',
  onValidationChange,
  onDataChange,
}) => {
  const { t } = useTranslation();
  const [selectedPayoutMethod, setSelectedPayoutMethod] = useState<
    'LocalBankTransfer' | 'InternationalBankTransfer' | ''
  >('LocalBankTransfer');
  const [isAsyncValidating, setIsAsyncValidating] = useState(false);

  // Schema parameters based on selections
  const schemaParams: RecipientSchemaParams = {
    currency,
    payout_method_type: selectedPayoutMethod,
  };

  // Fetch available payout methods
  const {
    data: payoutMethods,
    isLoading: isLoadingPayoutMethods,
    error: payoutMethodsError,
  } = useGetPayoutMethods();

  // Fetch countries
  const { data: countriesData, isLoading: isLoadingCountries } =
    useGetAllCountriesPhoenix({
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
    });
  const countries = useSortedCountries(countriesData ?? []);

  // Set default payout method when loaded
  useEffect(() => {
    if (payoutMethods && payoutMethods.length > 0) {
      // Always prefer LocalBankTransfer if available
      const localBankTransfer = payoutMethods.find(
        (method) => method === 'LocalBankTransfer'
      );
      if (localBankTransfer) {
        setSelectedPayoutMethod('LocalBankTransfer');
      } else if (!selectedPayoutMethod) {
        // Fallback to first available method only if no method is selected
        setSelectedPayoutMethod(
          payoutMethods[0] as 'LocalBankTransfer' | 'InternationalBankTransfer'
        );
      }
    }
  }, [payoutMethods, selectedPayoutMethod]);

  // Fetch recipient schema based on parameters
  const {
    data: schema,
    isLoading: isLoadingSchema,
    error: schemaError,
  } = useGetRecipientSchema(schemaParams, !!selectedPayoutMethod);

  const { mutateAsync: validateRecipientData } = useValidateRecipientData();

  const countryField = schema?.Country;
  const defaultCountry =
    countryField &&
    typeof countryField === 'object' &&
    'AllowedValues' in countryField &&
    Array.isArray(countryField.AllowedValues) &&
    countryField.AllowedValues.length > 0
      ? countryField.AllowedValues[0]
      : '';

  // Initialize formik with dynamic validation
  const formik = useFormik<RecipientData>({
    initialValues: {
      DisplayName: initialValues?.DisplayName || '',
      PayoutMethodType: (selectedPayoutMethod || 'LocalBankTransfer') as
        | 'LocalBankTransfer'
        | 'InternationalBankTransfer',
      RecipientType: recipientType,
      Currency: currency,
      Country: defaultCountry,
      ...initialValues,
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      // Build the complete request
      const request: CreateRecipientRequest = {
        currency,
        payout_method_type: selectedPayoutMethod,
        recipient_data: values,
      };

      await onSubmit(request);
    },
    validationSchema: Yup.object(), // Disable Yup validation since we're using async validation
    validate: async (values) => {
      // Only validate with API if we have the required params
      if (!schemaParams || !selectedPayoutMethod) {
        return {};
      }

      setIsAsyncValidating(true);
      try {
        const response = await validateRecipientData({
          ...schemaParams,
          recipient_data: values,
        });

        // If validation passes, return no errors
        if (response.valid) {
          return {};
        }
      } catch (error: any) {
        // Handle validation errors from the API
        if (error?.errors && Array.isArray(error.errors)) {
          // First, translate all error messages
          const translatedErrors = error.errors.map(
            (err: { field: string; message: string }) => {
              const translationKey = `mangopay.recipient.errors.${err.message}`;
              const translatedMessage = t(translationKey);

              // Special case: BIC errors should be shown on IBAN field
              let fieldPath = err.field;
              if (fieldPath.includes('.BIC')) {
                fieldPath = fieldPath.replace('.BIC', '.IBAN');
              }

              return {
                field: fieldPath,
                message:
                  translatedMessage !== translationKey
                    ? translatedMessage
                    : err.message,
              };
            }
          );

          // Transform flat errors to nested structure
          const nestedErrors = transformFlatErrorsToNested(translatedErrors);

          return nestedErrors;
        }
      } finally {
        setIsAsyncValidating(false);
      }

      // Return empty errors if something unexpected happens
      return {};
    },
    validateOnChange: false, // Only validate on blur, not on every change
    validateOnBlur: true,
  });

  // Destructure what we need from formik to avoid passing the entire object
  const {
    values,
    errors,
    touched,
    isValid,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
  } = formik;

  // Update Country field when schema loads or changes
  useEffect(() => {
    const countryField = schema?.Country;
    const countryFromSchema =
      countryField &&
      typeof countryField === 'object' &&
      'AllowedValues' in countryField &&
      Array.isArray(countryField.AllowedValues) &&
      countryField.AllowedValues.length > 0
        ? countryField.AllowedValues[0]
        : undefined;
    if (countryFromSchema && values.Country !== countryFromSchema) {
      setFieldValue('Country', countryFromSchema);
    }
  }, [schema, setFieldValue, values.Country]);

  // Update payout method type when selection changes
  useEffect(() => {
    if (selectedPayoutMethod) {
      setFieldValue('PayoutMethodType', selectedPayoutMethod);
    }
  }, [selectedPayoutMethod, setFieldValue]);

  // Notify parent about validation state changes
  useEffect(() => {
    if (onValidationChange) {
      const hasErrors = Object.keys(errors).length > 0;
      const touchedCount = Object.keys(touched).length;
      const formIsValid =
        isValid && touchedCount > 0 && !hasErrors && !isAsyncValidating;
      onValidationChange(formIsValid);
    }
  }, [isValid, errors, touched, isAsyncValidating, onValidationChange]);

  // Notify parent about data changes
  useEffect(() => {
    if (onDataChange) {
      onDataChange(values);
    }
  }, [values, onDataChange]);

  // Loading state
  if (isLoadingPayoutMethods || isLoadingSchema || isLoadingCountries) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (payoutMethodsError || schemaError) {
    return (
      <Alert severity="error">
        {t('service-provider-app.connect-account.schema-load-error')}
      </Alert>
    );
  }

  // No payout methods available
  if (!payoutMethods || payoutMethods.length === 0) {
    return (
      <Alert severity="warning">
        {t('service-provider-app.connect-account.no-payout-methods')}
      </Alert>
    );
  }

  return (
    <Stack gap={2}>
      {/* Show schema-based form when loaded */}
      {schema && selectedPayoutMethod && (
        <Box component="form" onSubmit={handleSubmit}>
          <DynamicRecipientForm
            schema={schema}
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            handleBlur={handleBlur}
            setFieldValue={setFieldValue}
            countryOptions={countries}
            disabled={disabled}
          />
        </Box>
      )}
    </Stack>
  );
};
